import { forwardRef, useId } from 'react';
import cx from 'classnames';
import './BrandCanvas.css';

/**
 * QHS BrandCanvas — animated brand surface used as the visual anchor
 * on the boot loader and the login brand panel.
 *
 * Renders a fixed midnight → deep → brand gradient with three soft-light
 * blended orbs that drift in slow, irregular orbits, plus a film-grain
 * noise overlay for texture. Always dark — the gradient is intentionally
 * locked across light/dark themes so the brand surface stays consistent.
 *
 * Props:
 *   - intensity: 'soft' (default, used inside loader/auth-shell) | 'flat'
 *                ('flat' kills the goo blur and orbs — use as a fallback)
 *   - className: additional classes
 *   - children:  rendered above the canvas, no extra wrapping
 */
const BrandCanvas = forwardRef(function BrandCanvas(
  { intensity = 'soft', className, children, ...rest },
  ref
) {
  const filterId = useId().replace(/[:]/g, '-');
  const noiseId = `${filterId}-noise`;
  const gooId = `${filterId}-goo`;

  return (
    <div
      ref={ref}
      className={cx('qhs-brand-canvas', `qhs-brand-canvas--${intensity}`, className)}
      {...rest}
    >
      <div className="qhs-brand-canvas__gradient" />
      {intensity === 'soft' && (
        <>
          <svg className="qhs-brand-canvas__defs" aria-hidden="true">
            <defs>
              <filter id={gooId}>
                <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
                <feColorMatrix
                  in="blur"
                  mode="matrix"
                  values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7"
                  result="goo"
                />
                <feBlend in="SourceGraphic" in2="goo" />
              </filter>
              <filter id={noiseId}>
                <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" />
                <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.05 0" />
              </filter>
            </defs>
          </svg>
          <div className="qhs-brand-canvas__orbs" style={{ filter: `url(#${gooId}) blur(40px)` }}>
            <div className="qhs-brand-canvas__orb qhs-brand-canvas__orb--1" />
            <div className="qhs-brand-canvas__orb qhs-brand-canvas__orb--2" />
            <div className="qhs-brand-canvas__orb qhs-brand-canvas__orb--3" />
          </div>
          <div
            className="qhs-brand-canvas__grain"
            style={{
              backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.05 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
            }}
          />
        </>
      )}
      {children && <div className="qhs-brand-canvas__content">{children}</div>}
    </div>
  );
});

export default BrandCanvas;
